/**
 * Informe de auditoría de la operación (Diego 2026-10-05), solo lectura, en ventana independiente
 * desde el DO, imprimible. Capítulos por tema y cada uno dice QUIÉN lo hace (Diego 2026-10-07):
 * Origen y los demás hallazgos los arma Customs AI; los umbrales de precio del Decreto 0214 los calcula
 * CIMA y aquí queda la constancia del cálculo (parámetros e ítem por ítem). Interno por ahora;
 * la exportación para el cliente viene después.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-10-05
 */
import React from "react";
import { Button } from "codekit";
import { useGetInformeAuditoriaQuery } from "../../../../libs/redux/slices/Modimpxx/Customsai/customsaiApiSlice";
import { useLazyExportarSerialdoQuery } from "../../../../libs/redux/slices/Modimpxx/Documexx/Serialdo/serialdoApiSlice";
import "../../../Tablerox/riodo.css";
import "./infaudit.css";

const TONO = { OK: "ok", REVISAR: "revisar", BLOQUEA: "bloquea", SIN_CERTIFICADO: "gris", NO_APLICA: "gris" };
const TEXTO_ESTADO = { OK: "Sin pendientes", REVISAR: "Requiere revisión", BLOQUEA: "Bloquea", SIN_CERTIFICADO: "Sin certificado", NO_APLICA: "No aplica" };
const TONO_SEM = { GREEN: "ok", YELLOW: "revisar", RED: "bloquea" };
const TONO_SEV = { BLOQUEANTE: "bloquea", ALTA: "bloquea", MEDIA: "revisar", INFO: "azul" };
const TONO_APLICA = { SI: "ok", NO: "gris", DUDA: "revisar" };
const TONO_HECHO = { "Customs AI": "ia", CIMA: "cima" };
const TONO_VEREDICTO = { OK: "ok", POR_DEBAJO: "bloquea", AUTORIZADO: "azul", EXENTO_ORIGEN: "azul", SIN_PESO: "revisar", SIN_TRM: "revisar" };
const TEXTO_VEREDICTO = { OK: "Por encima", POR_DEBAJO: "Por debajo", AUTORIZADO: "Autorizado", EXENTO_ORIGEN: "Exento por origen", SIN_PESO: "Sin peso o pares", SIN_TRM: "Sin tasa" };
const TEXTO_RC = { COMPLETO: "Cerrado", PENDIENTE: "Pendiente" };
const numeroCo = (valor, decimales = 2) =>
  valor === null || valor === undefined || valor === "" ? "—" : Number(valor).toLocaleString("es-CO", { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

const Chip = ({ tono = "gris", children }) => <span className={`inau-chip ${tono}`}>{children}</span>;
//Quién hace la sección: un chip por cada uno ("Customs AI + CIMA" son dos)
const QuienLoHace = ({ hechoPor }) => (hechoPor || "Customs AI").split(" + ").map((q) => <Chip key={q} tono={TONO_HECHO[q] || "gris"}>{q}</Chip>);
const TEXTO_MARCA = { SI: "Con beneficio", NO: "Sin beneficio", "SIN DECIDIR": "Por decidir" };
const TONO_MARCA = { SI: "ok", NO: "gris", "SIN DECIDIR": "revisar" };

const CapituloOrigen = ({ cap }) => (
  <>
    {cap.BLOQUES.map((b) => (
      <div className="inau-bloque" key={b.FACTURA.REDCIDXX}>
        <div className="inau-bloque-top">
          <div>
            <div className="inau-bloque-titulo">Factura {b.FACTURA.NUMERO || "sin número"}</div>
            <div className="inau-sub">
              {b.DECLARACION_EN_FACTURA?.DECLARA
                ? `La propia factura declara origen${b.DECLARACION_EN_FACTURA.ITEMS_CON_MARCA ? ` (${b.DECLARACION_EN_FACTURA.ITEMS_CON_MARCA} ítem(s) con marca PREF)` : ""}.`
                : "La factura no declara origen en su cuerpo."}
            </div>
          </div>
          {b.SIN_CERTIFICADO && <Chip tono="gris">Sin certificado de origen amarrado</Chip>}
        </div>
        {b.CERTIFICADOS.map((a) => (
          <div key={a.CERTIFICADO.REDCIDXX} style={{ marginTop: 10 }}>
            <div className="inau-bloque-top">
              <div>
                <span className="inau-bloque-titulo">Certificado {a.CERTIFICADO.NUMERO || "sin número"}</span>{" "}
                {a.GLOBAL && <Chip tono="azul">Global</Chip>}{" "}
                {a.ESTADO === "ANALIZADO" && <Chip tono={a.CUBRE === "SI" ? "ok" : a.CUBRE === "PARCIAL" ? "revisar" : "gris"}>Cubre la factura: {a.CUBRE}</Chip>}{" "}
                {a.ESTADO === "ANALIZADO" && <Chip tono={a.ACUERDO_SUGERIDO ? "azul" : "gris"}>{a.ACUERDO_SUGERIDO ? `Acuerdo sugerido ${a.ACUERDO_SUGERIDO}` : "Sin acuerdo con Colombia"}</Chip>}
              </div>
              {a.ESTADO === "ANALIZADO" && <span className="inau-sub">IA {a.MODELO} · {a.FECHA} · entrega a CIMA: {a.ENTREGA_CIMA}</span>}
            </div>
            <div className="inau-sub" style={{ marginTop: 4 }}>{a.RESUMEN}</div>
            {(a.ADVERTENCIAS || []).map((t, i) => <div className="inau-adv" key={i}>⚠ {t}</div>)}
            {a.ESTADO === "ANALIZADO" && (
              <table className="inau-tabla">
                <thead><tr><th>Ítem</th><th>Producto</th><th>Beneficio</th><th>Cómo lo amparó</th><th>Razón de la IA</th><th>Confianza</th></tr></thead>
                <tbody>
                  {a.ITEMS.map((it) => (
                    <tr key={it.ITEM}>
                      <td>{it.ITEM}</td>
                      <td><b>{it.CODIGO}</b><div className="inau-sub">{it.DESCRIPCION}</div></td>
                      <td><Chip tono={TONO_APLICA[it.APLICA] || "gris"}>{it.APLICA}</Chip></td>
                      <td>{it.COINCIDENCIA || "—"}{it.ITEM_CO ? <div className="inau-sub">{it.ITEM_CO}</div> : null}</td>
                      <td>{it.MOTIVO}</td>
                      <td>{it.CONFIANZA != null ? `${Math.round(it.CONFIANZA * 100)} %` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    ))}
    {cap.APLICADO_CIMA && (
      <div className="inau-bloque">
        <div className="inau-bloque-top">
          <div className="inau-bloque-titulo">Beneficio aplicado por ítem <Chip tono="cima">CIMA</Chip></div>
          <div>
            <Chip tono="ok">{cap.APLICADO_CIMA.SI} con beneficio</Chip> <Chip tono="gris">{cap.APLICADO_CIMA.NO} sin beneficio</Chip>{" "}
            {cap.APLICADO_CIMA.SIN_DECIDIR > 0 && <Chip tono="revisar">{cap.APLICADO_CIMA.SIN_DECIDIR} por decidir</Chip>}
          </div>
        </div>
        <div className="inau-sub" style={{ marginTop: 4 }}>
          Regla: la marca PREF de cada ítem en la factura manda; sin marca, decide la ficha del proveedor por país de origen (SIEMPRE APLICA = con beneficio, NO TIENE = sin beneficio, PREGUNTAR POR OPERACIÓN = lo responde el Ejecutivo en las Instrucciones o en la Prueba de origen). Customs AI puede cambiarla al analizar un certificado; una persona, en la Prueba de origen.
        </div>
        <table className="inau-tabla">
          <thead><tr><th>Proveedor</th><th>País de origen</th><th>Ficha del proveedor</th><th>Marca aplicada</th><th>Ítems</th></tr></thead>
          <tbody>
            {cap.APLICADO_CIMA.POLITICAS.map((p) => (
              <tr key={`${p.IDPROXXX}-${p.PAIIDXXX}-${p.MARCA}`}>
                <td>{p.PROVEEDOR || p.IDPROXXX}</td>
                <td>{p.PAIIDXXX || "—"}</td>
                <td>{p.PROPAICX}</td>
                <td><Chip tono={TONO_MARCA[p.MARCA] || "gris"}>{TEXTO_MARCA[p.MARCA] || p.MARCA}</Chip></td>
                <td>{p.ITEMS}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="inau-sub" style={{ marginTop: 6 }}>
          Instrucciones del cliente (RC 187): {cap.APLICADO_CIMA.MDIMBORI === "SI" ? "acoge el beneficio" : cap.APLICADO_CIMA.MDIMBORI === "NO" ? "no acoge el beneficio" : "sin respuesta"}
          {cap.APLICADO_CIMA.EXCLUIDAS?.length > 0 ? ` · facturas excluidas con autorización del cliente: ${cap.APLICADO_CIMA.EXCLUIDAS.join(", ")}` : ""}.
        </div>
      </div>
    )}
  </>
);

//Umbrales de precio del Decreto 0214: constancia del cálculo que hace CIMA (lo mismo que evalúa el RC 201)
const CapituloUmbrales = ({ cap }) => {
  const p = cap.PARAMETROS || {};
  const monedas = Object.keys(p.TRM || {});
  const porPar = cap.ITEMS.some((it) => it.UNIDAD_CALCULO === "PARES");
  return (
    <>
      <div className="inau-bloque">
        <div className="inau-bloque-titulo">Cómo se hizo el cálculo</div>
        <div className="inau-parametros">
          <div><span className="inau-sub">Tasa aduanera</span><div>{monedas.length === 0 ? "USD" : monedas.map((m) => `1 ${m} = ${numeroCo(p.TRM[m], 4)} USD (${p.FECHAS_TRM?.[m] ?? p.FECHA_TRM ?? ""})`).join(" · ")}</div></div>
          <div><span className="inau-sub">Peso bruto que vale</span><div>{numeroCo(p.PESO_BRUTO_DT, 2)} kg {p.FUENTE_PESO === "GUIA COMPLETA" ? "de la guía completa (los arribos aún no están repartidos)" : "del documento de transporte"}</div></div>
          <div><span className="inau-sub">Peso neto de los ítems</span><div>{numeroCo(p.PESO_NETO_ITEMS, 2)} kg · reparto del bruto por el neto de cada ítem</div></div>
          <div><span className="inau-sub">Flete y seguro del documento de transporte</span><div>{numeroCo(p.FLETE_DT, 2)} · {numeroCo(p.SEGURO_DT, 2)} {p.MONEDA_DT || ""} · prorrateados por valor</div></div>
          <div><span className="inau-sub">Términos de negociación</span><div>{(p.TERMINOS || []).join(", ") || "—"}</div></div>
          <div><span className="inau-sub">Resultado clave y autorización</span><div>RC {TEXTO_RC[cap.RC_ESTADO] || cap.RC_ESTADO || "sin abrir"} · autorización 130 {cap.AUTORIZACION?.PENDIENTE ? `pendiente (${cap.AUTORIZACION.CIACIDXX})` : "sin solicitud pendiente"}</div></div>
        </div>
      </div>
      {(cap.SECCIONES?.length > 0 ? cap.SECCIONES : [{ DECRETO: "", TITULO: "", REGLA: "", ITEMS: cap.ITEMS }]).map((seccion) => (
      <div key={seccion.DECRETO || "unico"} style={{ marginTop: 10 }}>
      {seccion.TITULO && <div className="inau-bloque-titulo">{seccion.TITULO} · {seccion.ITEMS.length} ítem(s)<div className="inau-sub" style={{ fontWeight: 400 }}>{seccion.REGLA}</div></div>}
      <table className="inau-tabla">
        <thead>
          <tr><th>Factura</th><th>Ítem Nº</th><th>Producto</th><th>Subpartida</th><th>Valor</th><th>Flete</th><th>Seguro</th><th>FOB</th><th>FOB USD</th><th>{seccion.DECRETO === "594" ? "Pares / kg" : "Peso bruto kg"}</th><th>USD / {porPar ? "par o kg" : "kg"}</th><th>Umbral</th><th>Veredicto</th></tr>
        </thead>
        <tbody>
          {seccion.ITEMS.map((it) => (
            <tr key={`${it.FACIDXXX}-${it.SECITEXX}`} className={it.VEREDICTO === "POR_DEBAJO" ? "inau-fila-alerta" : ""}>
              <td><b>{it.FACIDXXX}</b></td>
              <td><b>{it.SECITEXX}</b></td>
              <td><b>{it.PROIDXXX}</b><div className="inau-sub">{it.DESCRIPCION}</div></td>
              <td>{it.SUBIDXXX}</td>
              <td>{numeroCo(it.VALOR_ITEM)} {it.MONEDA}</td>
              <td>{numeroCo(it.FLETE)}</td>
              <td>{numeroCo(it.SEGURO)}</td>
              <td>{numeroCo(it.FOB)}</td>
              <td>{numeroCo(it.FOB_USD)}</td>
              <td>{it.UNIDAD_CALCULO === "PARES" ? `${numeroCo(it.PARES, 0)} pares` : `${numeroCo(it.PESO_BRUTO, 3)}${it.PESO_BRUTO_PROPIO ? "" : " *"}`}</td>
              <td><b>{numeroCo(it.PRECIO, 4)}</b>{it.UNIDAD_CALCULO === "PARES" ? " /par" : ""}</td>
              <td>{numeroCo(it.UMBRAL)}</td>
              <td><Chip tono={TONO_VEREDICTO[it.VEREDICTO] || "gris"}>{TEXTO_VEREDICTO[it.VEREDICTO] || it.VEREDICTO}</Chip>{it.MODALIDAD ? <div className="inau-sub" title={it.MODALIDAD_MOTIVO}>Modalidad {it.MODALIDAD}</div> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      ))}
      <div className="inau-sub" style={{ marginTop: 6 }}>* Peso bruto repartido por CIMA desde el documento de transporte (el ítem no trae peso bruto propio).</div>
      {cap.FUERA?.length > 0 && (
        <div className="inau-bloque">
          <div className="inau-bloque-titulo">Ítems que no están en partidas del decreto ({cap.FUERA.length})</div>
          <div className="inau-sub" style={{ marginTop: 2 }}>No se evalúan: su partida no está en el Decreto 0214 ni en el 594. Se listan para que no falte ningún ítem de la factura en esta constancia.</div>
          {Object.entries(cap.FUERA.reduce((acc, it) => ({ ...acc, [it.FACIDXXX]: [...(acc[it.FACIDXXX] || []), it] }), {})).map(([factura, items]) => (
            <div key={factura} style={{ marginTop: 6, fontSize: 13 }}>
              <b>Factura {factura}</b>: {items.map((it) => `${it.SECITEXX} (${it.PARTIDA || "sin subpartida"})`).join(" · ")}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

//¿Están todas las facturas? (Diego 2026-10-07): referencias que relacionan la guía y la lista de empaque, cuáles
//tienen factura y cuáles no, y el peso de la guía contra el de las facturas
const EvidenciaFacturasFaltantes = ({ ev }) => {
  const sinFactura = new Set(ev.REFERENCIAS_SIN_FACTURA || []);
  return (
    <div className="inau-parametros" style={{ marginTop: 8 }}>
      <div>
        <span className="inau-sub">Referencias que relacionan el transporte y la lista de empaque</span>
        <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(ev.REFERENCIAS_RELACIONADAS || []).map((r) => <Chip key={r} tono={sinFactura.has(r) ? "bloquea" : "ok"}>{r}{sinFactura.has(r) ? " · sin factura" : " · con factura"}</Chip>)}
          {(ev.REFERENCIAS_RELACIONADAS || []).length === 0 && <span className="inau-sub">Ninguna referencia legible.</span>}
        </div>
      </div>
      <div>
        <span className="inau-sub">Facturas en la operación</span>
        <div>{(ev.FACTURAS_PRESENTES || []).join(", ") || "—"}</div>
      </div>
      <div>
        <span className="inau-sub">Peso bruto: transporte frente a facturas</span>
        <div>
          {numeroCo(ev.PESO_TRANSPORTE_KG, 0)} kg del transporte · {numeroCo(ev.PESO_FACTURAS_KG, 0)} kg de las facturas
          {ev.EXCESO_KG != null ? ` · ${numeroCo(ev.EXCESO_KG, 0)} kg (${numeroCo(ev.EXCESO_PCT, 0)} %) sin factura que lo explique` : ""}
          {ev.FACTURAS_SIN_PESO?.length ? ` · sin peso declarado: ${ev.FACTURAS_SIN_PESO.join(", ")}` : ""}
        </div>
      </div>
    </div>
  );
};

/** Capítulo de seriales (CIMA): por ítem y variable, cuántos exige, cuántos tiene, de qué fuente y si cuadra */
const CapituloSeriales = ({ cap, DOCNROXX }) => {
  const [exportar, { isFetching }] = useLazyExportarSerialdoQuery();
  const handleExportar = () => exportar(DOCNROXX).unwrap().then((blob) => {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `seriales_${DOCNROXX}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }).catch(() => {});
  return (
  <div className="inau-bloque">
    <div className="inau-sub" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span>La lista completa de seriales no va en el informe (pueden ser miles): se descarga aparte.</span>
      <Button type="button" size="s" variant="secondary-light" icon="download" isLoading={isFetching} onClick={handleExportar}>Descargar los seriales (Excel)</Button>
    </div>
    <table className="inau-tabla">
      <thead>
        <tr><th>Factura</th><th>Ítem</th><th>Producto</th><th>Variable</th><th>Exige</th><th>Tiene</th><th>Repetidos</th><th>Fuente</th><th>Cuadra</th></tr>
      </thead>
      <tbody>
        {(cap.ITEMS || []).map((it) => (
          <tr key={`${it.FACTURA}-${it.ITEM}-${it.VARIABLE}`}>
            <td>{it.FACTURA}</td><td>{it.ITEM}</td><td>{it.PRODUCTO}</td><td>{it.VARIABLE}</td>
            <td>{numeroCo(it.REQUERIDOS, 0)}</td><td>{numeroCo(it.CARGADOS, 0)}</td><td>{numeroCo(it.REPETIDOS, 0)}</td>
            <td>{Object.entries(it.FUENTES || {}).map(([f, c]) => `${numeroCo(c, 0)} ${f}`).join(" · ") || "—"}</td>
            <td><Chip tono={it.CUADRA === "SI" ? "ok" : "revisar"}>{it.CUADRA}</Chip></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const CapituloHallazgos = ({ cap }) => (
  <>
    {cap.BLOQUES.map((h, i) => (
      <div className="inau-bloque inau-hallazgo" key={`${h.REGLA}-${i}`}>
        <div className="inau-bloque-top">
          <div className="inau-hallazgo-titulo">{h.TITULO}</div>
          <div><Chip tono={TONO_SEV[h.SEVERIDAD] || "gris"}>{h.SEVERIDAD}</Chip> <Chip tono="gris">{h.ESTADO}</Chip></div>
        </div>
        <div style={{ marginTop: 6, fontSize: 13 }}>{h.DESCRIPCION}</div>
        {h.REGLA === "FACTURAS_FALTANTES" && h.EVIDENCIAS && <EvidenciaFacturasFaltantes ev={h.EVIDENCIAS} />}
        {h.DOCUMENTOS?.length > 0 && <div className="inau-sub" style={{ marginTop: 4 }}>Documentos: {h.DOCUMENTOS.map((d) => `${d.TIPO || ""} ${d.NUMERO || d.REDCIDXX}`).join(" · ")}</div>}
      </div>
    ))}
  </>
);

const Infaudit = ({ DOCNROXX, onVolver }) => {
  const { data, isFetching, isError } = useGetInformeAuditoriaQuery(DOCNROXX, { skip: !DOCNROXX });
  const informe = data?.data ?? null;
  const op = informe?.OPERACION ?? null;
  const capitulos = informe?.CAPITULOS ?? [];
  const sinNada = !isFetching && capitulos.length === 0;
  return (
    <div className="riodo inau-lienzo">
      <div className="rio-band">
        <div>
          <div className="rio-band-sub">Auditoría de la operación · interno · {(informe?.HECHO_POR || []).join(" + ") || "Customs AI + CIMA"}</div>
          <div className="rio-band-title">DO {DOCNROXX}{informe?.PARTES?.CLIENTE?.NOMBRE ? ` · ${informe.PARTES.CLIENTE.NOMBRE}` : op ? ` · cliente ${op.CLIIDXXX}` : ""}</div>
        </div>
        <div className="inau-noprint" style={{ display: "flex", gap: 8 }}>
          <Button type="button" variant="secondary-bold" size="m" onClick={() => window.print()}>Imprimir</Button>
          {onVolver && <Button type="button" variant="secondary-bold" size="m" onClick={onVolver}>Cerrar</Button>}
        </div>
      </div>

      {isFetching && <div className="inau-vacio">Cargando el informe…</div>}
      {isError && <div className="inau-vacio">No fue posible leer el informe de auditoría.</div>}
      {sinNada && !isError && <div className="inau-vacio">{informe?.MENSAJE || data?.warning || "Esta operación no tiene todavía secciones de auditoría."}</div>}
      {!isFetching && !op && capitulos.length > 0 && <div className="inau-adv" style={{ marginTop: 10 }}>⚠ {informe?.MENSAJE || data?.warning || "Customs AI no tiene una operación para este DO."} Las secciones de Customs AI no están en este informe.</div>}
      {op && informe?.OPERACION_MOTOR_DE && <div className="inau-nota">Las secciones de Customs AI son el análisis de los documentos de la operación {informe.OPERACION_MOTOR_DE}, de la que nació este DO (misma factura y guía dividida por el transportador).</div>}

      {op && (
        <>
          <div className="inau-cabecera">
            {[
              ["Semáforo", <Chip tono={TONO_SEM[op.SEMAFORO] || "gris"}>{op.SEMAFORO || "—"}</Chip>],
              ["Revisión humana", op.REVISION_HUMANA ? "Sí" : "No"],
              ["Versión del análisis", op.VERSION_AUDITORIA || "—"],
              ["Documentos auditados", informe.DOCUMENTOS.length],
              ["Operación en el motor", `#${op.CUSOIDXX} · ${op.CREADA}`],
              ["Generado", informe.GENERADO],
            ].map(([t, v]) => (
              <div className="rio-card" key={t} style={{ padding: "10px 14px" }}>
                <div className="rio-kicker">{t}</div>
                <div className="rio-strip-valor" style={{ fontSize: 15 }}>{v}</div>
              </div>
            ))}
          </div>

          {informe.PARTES && (
            <div className="rio-card" style={{ padding: "10px 14px" }}>
              <div className="rio-kicker">Cliente y proveedores</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                <span style={{ marginRight: 16 }}><b>Cliente</b> {informe.PARTES.CLIENTE?.NOMBRE || informe.PARTES.CLIENTE?.CLIIDXXX || "—"}</span>
                {Object.entries(informe.PARTES.PROVEEDORES || {}).map(([codigo, nombre]) => <span key={codigo} style={{ marginRight: 16 }}><b>Proveedor</b> {nombre}</span>)}
              </div>
            </div>
          )}

          <div className="rio-card" style={{ padding: "10px 14px" }}>
            <div className="rio-kicker">Documentos</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {informe.DOCUMENTOS.map((d) => <span key={d.REDCIDXX} style={{ marginRight: 12 }}><b>{d.TIPO}</b> {d.NUMERO || "—"}{d.VERSION > 1 ? ` (v${d.VERSION})` : ""}</span>)}
            </div>
          </div>

        </>
      )}

      {capitulos.length > 0 && (
        <>
          {informe.PENDIENTES?.length > 0 && (
            <div className="rio-card inau-cap">
              <div className="inau-cap-titulo">Pendientes para una persona</div>
              {informe.PENDIENTES.map((p, i) => <div className="inau-pend" key={i}>{p}</div>)}
            </div>
          )}

          {capitulos.map((cap) => (
            <div className="rio-card inau-cap" key={cap.CLAVE}>
              <div className="inau-cap-top">
                <div>
                  <div className="inau-cap-titulo">{cap.TITULO} <QuienLoHace hechoPor={cap.HECHO_POR} /></div>
                  <div className="inau-cap-resumen">{cap.RESUMEN}</div>
                </div>
                <Chip tono={TONO[cap.ESTADO] || "gris"}>{TEXTO_ESTADO[cap.ESTADO] || cap.ESTADO}</Chip>
              </div>
              {cap.CLAVE === "ORIGEN" && <CapituloOrigen cap={cap} />}
              {cap.CLAVE === "UMBRALES" && <CapituloUmbrales cap={cap} />}
              {cap.CLAVE === "SERIALES" && <CapituloSeriales cap={cap} DOCNROXX={DOCNROXX} />}
              {!["ORIGEN", "UMBRALES", "SERIALES"].includes(cap.CLAVE) && <CapituloHallazgos cap={cap} />}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default Infaudit;
